// D3D-774 v1.2: 仮設道路AI の feature facade。
// .vue（AiChatbot）から多段ワークフロー（検査 → store 更新 → 実行 → 保存 → サマリ生成）を
// 切り出し、View は本 facade の呼び出しに留める（client-architecture の責務分離）。

import { convertCoordinatesToPointCloudCrs } from "@/utils/cesium-common";
import { notifyWarning } from "@/utils/notificationUtils";
import { runPrototypeRoadAI } from "@/utils/prototypeRoadAIUtils";
import {
	ROAD_AI_MAX_PICK_POINTS,
	ROAD_AI_MAX_WAYPOINTS,
	ROAD_AI_RUN_ERROR_CODES,
	type RoadAiCandidateState,
	type RoadAiChatbotResponse,
	type RoadAiDataInspection,
	type RoadAiDiagnostic,
	type RoadAiRunErrorCode,
	filterObjectivesForMode,
} from "./roadAi.types";
import {
	type InspectVisibleTerrainInput,
	inspectVisibleTerrain,
	inspectionMode,
} from "./roadAiDataInspection";
import {
	buildBlockedGuideMessage,
	buildDataSelectionMessage,
	buildNoCandidateMessage,
	buildRunErrorMessage,
} from "./roadAiMessages";

// 始終点ピック中に適用する設計データの透明度（点群上をピックできるよう半透明化する）。
const START_END_PICK_DESIGN_TRANSPARENCY = 0.2;

// Toast shown on every click beyond the pick limit (D3D-852). Auto-dismissed so repeated clicks don't pile up.
const PICK_LIMIT_EXCEEDED_MESSAGE = `中継点は最大${ROAD_AI_MAX_WAYPOINTS}点です（全体で最大${ROAD_AI_MAX_PICK_POINTS}点）。これ以上点を追加できません。右クリックして「完了」を選んでください。`;
const PICK_LIMIT_EXCEEDED_TOAST_MS = 3_000;

/** Options API（this.$store）からも使えるよう、必要最小の Vuex API のダックタイプ。 */
export interface RoadAiStoreLike {
	getters: Readonly<Record<string, unknown>>;
	dispatch: (type: string, payload?: unknown) => unknown;
	commit: (type: string, payload?: unknown) => void;
}

/** 半透明化に使う設計モデルの最小 API（ダックタイプ）。 */
interface DesignTransparencyLike {
	getAssetIdNum(): number;
	getTransparency(): number;
}

/** store getter の値を配列として取り出す（未初期化は空配列）。 */
const toArray = <T>(value: unknown): readonly T[] =>
	Array.isArray(value) ? (value as T[]) : [];

/** 例外から実行中止コードを取り出す（未知の形は ROUTE_ENGINE_FAILED に畳む）。 */
const toRunErrorCode = (error: unknown): RoadAiRunErrorCode => {
	const code =
		typeof error === "object" && error !== null && "code" in error
			? (error as { code?: unknown }).code
			: undefined;
	return typeof code === "string" &&
		(ROAD_AI_RUN_ERROR_CODES as readonly string[]).includes(code)
		? (code as RoadAiRunErrorCode)
		: "ROUTE_ENGINE_FAILED";
};

/** 実行結果の会話サマリをフロントで決定論的に生成する（LLM 再呼び出しはしない）。 */
const buildResultSummary = (
	candidateState: Readonly<RoadAiCandidateState>,
	diagnostics: readonly RoadAiDiagnostic[],
): string => {
	if (candidateState.ids.length > 0) {
		return `${candidateState.ids.length}件の候補を一覧に表示しました。`;
	}
	const first = diagnostics.length > 0 ? diagnostics[0] : null;
	// 候補 0 件は見直しを促す（AC-37）。
	return buildNoCandidateMessage(first?.message || first?.code || "");
};

/**
 * 仮設道路AI の feature facade。表示中データ検査・LLM 応答の反映・始終点選択・
 * 経路生成（受付 → ポーリング）の各コマンドを store 連携込みで提供する。
 */
export const useRoadAi = (store: RoadAiStoreLike) => {
	// アシスタント発話を表示用・送信用の両コンテキストへ追加する
	const pushAssistant = (text: string): void => {
		store.dispatch("addRoadTerrainByAi/add_chat_message", {
			role: "assistant",
			text,
		});
		store.dispatch("addRoadTerrainByAi/add_post_chat_message", {
			role: "assistant",
			text,
		});
	};

	// 会話コンテキストをクリアする。点群のみモードの明示指定（pointCloudOnlyRequested）も
	// ここで解除し、次の検討へ暗黙に持ち越さない（AC-35 の明示指定は会話単位）。
	const clearConversation = (): void => {
		store.dispatch("addRoadTerrainByAi/clear_chat_context");
		store.dispatch("addRoadTerrainByAi/clear_post_chat_context");
		store.dispatch(
			"addRoadTerrainByAi/update_point_cloud_only_requested",
			false,
		);
	};

	// 表示中データを検査してモードと使用データを確定する（AC-1 / AC-2 / AC-13 / AC-35）
	const inspectVisibleData = (): RoadAiDataInspection =>
		inspectVisibleTerrain({
			pointClouds: toArray<InspectVisibleTerrainInput["pointClouds"][number]>(
				store.getters.visiblePointClouds,
			),
			designs: toArray<InspectVisibleTerrainInput["designs"][number]>(
				store.getters.visibleDesigns,
			),
			pointCloudOnlyRequested:
				store.getters["addRoadTerrainByAi/pointCloudOnlyRequested"] === true,
		});

	// 検査結果を store へ反映する（モード・使用データ）
	const applyInspection = (inspection: RoadAiDataInspection): void => {
		store.dispatch(
			"addRoadTerrainByAi/update_mode",
			inspectionMode(inspection),
		);
		store.dispatch(
			"addRoadTerrainByAi/update_data_selection",
			inspection.status === "ok" ? inspection.selection : null,
		);
	};

	// LLM 応答から抽出したパラメータ・点群のみ指定を store へ反映する（AC-35）
	const applyChatbotResponse = (response: RoadAiChatbotResponse): void => {
		if (Object.hasOwn(response, "objectives")) {
			store.dispatch(
				"addRoadTerrainByAi/update_objectives",
				response.objectives,
			);
		}
		if (Object.hasOwn(response, "road_width_m")) {
			store.dispatch(
				"addRoadTerrainByAi/update_road_width_m",
				Number(response.road_width_m),
			);
		}
		if (Object.hasOwn(response, "max_slope_percent")) {
			store.dispatch(
				"addRoadTerrainByAi/update_max_slope_percent",
				Number(response.max_slope_percent),
			);
		}
		if (response.use_point_cloud_only === true) {
			store.dispatch(
				"addRoadTerrainByAi/update_point_cloud_only_requested",
				true,
			);
		}
	};

	// 始終点選択を起動する（表示中データを検査してモードを確定。AC-3）
	const startPointSelection = async (): Promise<void> => {
		const inspection = inspectVisibleData();
		applyInspection(inspection);
		if (inspection.status === "blocked") {
			pushAssistant(buildBlockedGuideMessage(inspection.reason));
			return;
		}
		// 使用データ確認メッセージ（1 回目・始終点選択の前）。同意応答は求めない（AC-3）。
		pushAssistant(
			buildDataSelectionMessage(inspection.selection, "before_pick"),
		);

		// --- 点群上をピックできるよう設計データを半透明化し、finally で元の透明度に戻す。
		// 点群のみモードでは設計が表示されていない（または使用しない）ため半透明化しない。
		const visibleDesigns = toArray<DesignTransparencyLike>(
			store.getters.visibleDesigns,
		);
		const design =
			inspection.selection.mode === "standard" && visibleDesigns.length === 1
				? visibleDesigns[0]
				: null;
		const originalTransparency = design ? design.getTransparency() : null;
		if (design) {
			window.viewer.updateTilesetTransparency(
				design.getAssetIdNum(),
				"design",
				START_END_PICK_DESIGN_TRANSPARENCY,
			);
		}
		try {
			// --- 始点・中継点・終点を1回の連続ピックで取得（右クリック→完了で最後の点＝終点）。
			const picked = await new Promise<readonly unknown[]>((resolve) => {
				window.viewer.enableRoadAiPointPick(
					(points: readonly unknown[]) => resolve(points),
					ROAD_AI_MAX_PICK_POINTS,
					() =>
						notifyWarning(
							{ $store: store },
							PICK_LIMIT_EXCEEDED_MESSAGE,
							PICK_LIMIT_EXCEEDED_TOAST_MS,
						),
				);
			});
			if (!picked || picked.length < 2) {
				pushAssistant(
					"始点・終点を含む2点以上をクリックし、右クリックで完了してください。",
				);
				return;
			}
			const local = await convertCoordinatesToPointCloudCrs(picked);
			if (!local || local.length < 2) {
				pushAssistant("座標変換に失敗しました。もう一度お試しください。");
				return;
			}
			const toXYZ = (p: { x: number; y: number; z: number }) => [p.x, p.y, p.z];
			store.dispatch("addRoadTerrainByAi/update_ai_start", toXYZ(local[0]));
			store.dispatch(
				"addRoadTerrainByAi/update_ai_end",
				toXYZ(local[local.length - 1]),
			);
			const waypoints = local.slice(1, -1).map(toXYZ);
			store.dispatch("addRoadTerrainByAi/update_waypoints", waypoints);
			// 点選択完了を LLM に通達する（座標値は渡さない）
			const wpMsg = waypoints.length ? `・中継点${waypoints.length}点` : "";
			pushAssistant(
				`始点・終点${wpMsg}を設定しました。通行する建機や目的（切土/盛土/バランスなど）を教えてください。`,
			);
		} catch (error: unknown) {
			console.error("Error selecting start/end points:", error);
			pushAssistant("点の選択に失敗しました。もう一度お試しください。");
		} finally {
			// キャンセル・失敗・完了のいずれでも元の透明度に戻す。
			if (design) {
				window.viewer.updateTilesetTransparency(
					design.getAssetIdNum(),
					"design",
					originalTransparency,
				);
			}
		}
	};

	// 会話条件が揃ったら経路生成を実行する（受付 → ポーリング）
	const runRoadAi = async (siteId: string | undefined): Promise<void> => {
		// 実行中は多重実行を防止する（AC-18）
		if (store.getters["addRoadTerrainByAi/isRunning"] === true) return;
		const start = store.getters["addRoadTerrainByAi/aiStart"];
		const end = store.getters["addRoadTerrainByAi/aiEnd"];
		if (!Array.isArray(start) || !Array.isArray(end)) {
			pushAssistant(
				"始点・終点が設定されていません。先に始点・終点を指定してください。",
			);
			return;
		}
		// --- 実行直前に再検査して使用データを確定する（表示が変わっている場合に備える。AC-12）
		const inspection = inspectVisibleData();
		applyInspection(inspection);
		if (inspection.status === "blocked") {
			pushAssistant(buildBlockedGuideMessage(inspection.reason));
			return;
		}
		const selection = inspection.selection;
		// 使用データ確認メッセージ（2 回目・実行直前）。同意応答は求めない（AC-36）。
		pushAssistant(buildDataSelectionMessage(selection, "before_run"));

		if (!siteId) {
			pushAssistant(buildRunErrorMessage("ROUTE_ENGINE_FAILED"));
			return;
		}

		// --- 実行（受付 → ポーリング）と結果の保存
		store.dispatch("addRoadTerrainByAi/update_run_error", null);
		store.dispatch("addRoadTerrainByAi/update_is_running", true);
		try {
			const data = await runPrototypeRoadAI({
				siteId,
				selection,
				start,
				end,
				roadWidthM: store.getters["addRoadTerrainByAi/roadWidthM"],
				slopeRatio: 0.1,
				maxSlopePercent: store.getters["addRoadTerrainByAi/maxSlopePercent"],
				// 点群のみモードでは設計依存の目的を外す（AC-23 / AC-26）
				objectives: filterObjectivesForMode(
					toArray(store.getters["addRoadTerrainByAi/objectives"]),
					selection.mode,
				),
				cfDiffEvaluator: "proxy",
				topKPerObjective: 1,
				searchBackend: "hybrid_a_star",
				objectiveWeight: 1.0,
				waypoints: toArray(store.getters["addRoadTerrainByAi/waypoints"]),
			});
			// 正規化状態は runPrototypeRoadAI が返すものをそのまま保存する（再正規化しない）。
			const candidateState: RoadAiCandidateState = data.candidateState ?? {
				ids: [],
				entities: {},
			};
			const diagnostics: RoadAiDiagnostic[] = Array.isArray(data.diagnostics)
				? data.diagnostics
				: [];
			store.dispatch(
				"addRoadTerrainByAi/update_candidate_state",
				candidateState,
			);
			store.dispatch("addRoadTerrainByAi/update_diagnostics", diagnostics);
			store.dispatch(
				"addRoadTerrainByAi/update_used_source",
				data.usedSource || null,
			);
			// 実行後サマリはフロントで決定論的に生成する（LLM 再呼び出しはしない）
			pushAssistant(buildResultSummary(candidateState, diagnostics));
		} catch (error: unknown) {
			console.error("Error running road AI:", error);
			const code = toRunErrorCode(error);
			store.dispatch("addRoadTerrainByAi/update_run_error", code);
			pushAssistant(buildRunErrorMessage(code));
		} finally {
			// 決着（成功・失敗・中断）でロックを解除する（AC-41）。
			store.dispatch("addRoadTerrainByAi/update_is_running", false);
		}
	};

	return {
		pushAssistant,
		clearConversation,
		inspectVisibleData,
		applyInspection,
		applyChatbotResponse,
		startPointSelection,
		runRoadAi,
	};
};
